import {
  DateTimeIsoString,
  EntityId,
  FormType,
  isFormType,
} from '@fmnts/api/shared';
import { Option } from '@fmnts/core';
import { Requirement } from './requirement';

export type FormId = NextFormMinimal['id'];

const FORM_REF_DELIMITER = '/';
/**
 * Used to uniquely identify a form.
 */
export type FormRef = `${FormType}${typeof FORM_REF_DELIMITER}${FormId}`;
type FormRefObj = Pick<NextFormMinimal, 'id' | 'formType'>;

export interface NextFormMinimal {
  /**
   * The id of the forms are not unique across formTypes,
   * a combination of formType/id is used to uniquely identify them
   */
  id: EntityId;
  formType: FormType;
  logo: Option<string>;
  modifytime: DateTimeIsoString;
  name: string;
  organizationLogo: Option<string>;
  requirements: readonly Requirement[];
  supportedLanguages: Record<string, string>;
  useNextForm: boolean;
}

/**
 * Utility function to transform a form to a form ref.
 */
export function formReference({ id, formType: type }: FormRefObj): FormRef {
  return `${type}${FORM_REF_DELIMITER}${id}`;
}

export function deserializeFormReference(ref: FormRef): FormRefObj {
  const [formType, id] = ref.split(FORM_REF_DELIMITER);

  if (!isFormType(formType)) {
    throw new Error();
  }

  return { id, formType };
}

/**
 * Creates a helper function to filter forms by form type.
 *
 * @param type The type to filter for
 *
 * @returns
 * Function that when called with a next form returns
 * `true` if the form type of the passed form
 */
export function byFormType(
  type: FormType,
): <TForm extends NextFormMinimal>(form: TForm) => boolean;
export function byFormType<T extends FormType>(
  type: T,
): <TForm extends NextFormMinimal>(
  form: TForm,
) => form is TForm & Record<'formType', T>;
export function byFormType(type: FormType): (f: NextFormMinimal) => boolean {
  return ({ formType }) => formType === type;
}
